import * as React from "react";

import type { FeatureSignupSource } from "@degaus/shared/feature-signups";

import { useAnalytics } from "@/common/analytics";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { api } from "@/trpc/react";

// Interest capture for pre-launch features (e.g. MCP, ready-made automations):
// a CTA opens an email-capture dialog instead of a checkout. The cloud-twin
// Premium tier used this too until cloud twins were parked; no pricing surface
// sells or advertises them anymore.
export const EARLY_ACCESS_CTA_LABEL = "Get early access";

// Which pricing surface the CTA lives on, forwarded to PostHog so click intent
// can be sliced per surface for validation.
export type EarlyAccessSurface =
  | "landing_pricing"
  | "twin_landing"
  | "onboarding_paywall"
  | "account_plan"
  | "subscription_dialog"
  | "faq";

/**
 * Drives the early-access CTA on a surface. `openEarlyAccess` records the click
 * in PostHog (for validation) then opens the email-capture dialog; render
 * `dialog` once in the surface. The caller keeps its own button so each
 * surface's native styling is preserved. `featureSource` tags the captured
 * email so demand can be sliced per pre-launch feature.
 */
export function useEarlyAccess(
  surface: EarlyAccessSurface,
  featureSource: FeatureSignupSource,
) {
  const [open, setOpen] = React.useState(false);
  const trackEvent = useAnalytics();

  const openEarlyAccess = React.useCallback(() => {
    trackEvent("early access clicked", { surface, featureSource });
    setOpen(true);
  }, [trackEvent, surface, featureSource]);

  const dialog = (
    <EarlyAccessDialog
      open={open}
      onOpenChange={setOpen}
      source={featureSource}
    />
  );

  return { open, openEarlyAccess, dialog };
}

function EarlyAccessDialog({
  open,
  onOpenChange,
  source,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: FeatureSignupSource;
}) {
  // A per-open session id, derived during render (React's "adjusting state
  // during render" pattern) so the form is already mounted on the FIRST paint
  // of each open — no blank frame — with a fresh key per open to reset
  // email/submitted state. It stays mounted through the close animation so
  // the dialog doesn't empty while animating out, and stays out of the
  // never-opened / SSR-prerendered tree, whose missing client-only tRPC
  // provider would crash its api.*.useMutation.
  const [session, setSession] = React.useState(0);
  const [prevOpen, setPrevOpen] = React.useState(false);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setSession((count) => count + 1);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-4 border-[var(--cli-border)] bg-[var(--cli-bg-elevated)] text-[var(--cli-text)] shadow-[0_24px_80px_rgba(0,0,0,0.55)] sm:max-w-md">
        {session > 0 && (
          <EarlyAccessForm
            key={session}
            source={source}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function EarlyAccessForm({
  source,
  onClose,
}: {
  source: FeatureSignupSource;
  onClose: () => void;
}) {
  const trackEvent = useAnalytics();
  const [email, setEmail] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);

  const { mutate, isPending } = api.featureSignups.create.useMutation({
    onSuccess: () => {
      trackEvent("feature signup submitted", { source });
      setSubmitted(true);
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    mutate({ email: trimmed, source });
  };

  if (submitted) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Thanks for your interest</DialogTitle>
          <DialogDescription className="text-sm leading-6 text-[var(--cli-text-secondary)]">
            You&apos;re on the list. We&apos;ll be in touch soon.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end">
          <Button type="button" variant="steward" onClick={onClose}>
            Got it
          </Button>
        </div>
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <DialogHeader>
        <DialogTitle>Get priority access</DialogTitle>
        <DialogDescription className="text-sm leading-6 text-[var(--cli-text-secondary)]">
          Drop your email and we&apos;ll reach out as soon as it&apos;s ready.
        </DialogDescription>
      </DialogHeader>
      <Input
        type="email"
        required
        autoFocus
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        placeholder="you@company.com"
        aria-label="Email address"
        className="mt-4"
      />
      <div className="mt-4 flex justify-end">
        <Button type="submit" variant="steward" disabled={isPending}>
          {isPending ? "Submitting..." : "Request access"}
        </Button>
      </div>
    </form>
  );
}
